import type { ReactNode } from 'react';
import { cn, GLYPH } from '@/shared/lib';
import { Switch } from './Switch';

/**
 * Мелкие элементы конструкторов обучения (прототип: программа курса, вопросы теста): метка 20
 * (radius 16, рамка и текст — цвет тона, 10/500), кнопки порядка ↑/↓ 24×24, переключатель с
 * подписью 11 ink-600, текстовое действие 11 status-error.
 */
export const TAG_TONES = {
  ok: 'border-tone-ok-fg bg-tone-ok-bg text-tone-ok-fg',
  bad: 'border-tone-bad-fg bg-tone-bad-bg text-tone-bad-fg',
  gold: 'border-tone-gold-fg bg-tone-gold-bg text-tone-gold-fg',
  mute: 'border-ink-500 bg-surface-sunken text-ink-500',
} as const;

export type ToneTagTone = keyof typeof TAG_TONES;

export function ToneTag({ tone, children }: { tone: ToneTagTone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex h-20 shrink-0 items-center rounded-16 border px-7 text-[10px] font-medium whitespace-nowrap',
        TAG_TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

export interface OrderButtonsProps {
  upLabel: string;
  downLabel: string;
  onUp: () => void;
  onDown: () => void;
  canUp: boolean;
  canDown: boolean;
}

export function OrderButtons({
  upLabel,
  downLabel,
  onUp,
  onDown,
  canUp,
  canDown,
}: OrderButtonsProps) {
  const base =
    'flex size-24 shrink-0 cursor-pointer items-center justify-center rounded-16 border border-line-strong bg-paper-pure text-[11px] text-ink-900 hover:bg-paper-deep disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <>
      <button
        type="button"
        title={upLabel}
        aria-label={upLabel}
        onClick={onUp}
        disabled={!canUp}
        className={base}
      >
        {GLYPH.up}
      </button>
      <button
        type="button"
        title={downLabel}
        aria-label={downLabel}
        onClick={onDown}
        disabled={!canDown}
        className={base}
      >
        {GLYPH.down}
      </button>
    </>
  );
}

export interface LabeledSwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  /** Подпись для экранного диктора: что именно переключается. */
  ariaLabel: string;
  /** Ширина подписи — чтобы строки не прыгали (прототип: 88 у программы, 118 у вопросов). */
  labelWidth?: 'narrow' | 'wide';
  disabled?: boolean;
}

export function LabeledSwitch({
  checked,
  onCheckedChange,
  label,
  ariaLabel,
  labelWidth = 'narrow',
  disabled,
}: LabeledSwitchProps) {
  return (
    <span className="flex shrink-0 items-center gap-7">
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={ariaLabel}
        disabled={disabled}
      />
      <span
        aria-hidden="true"
        className={cn(
          'text-[11px] whitespace-nowrap text-ink-600',
          labelWidth === 'wide' ? 'min-w-118' : 'min-w-88',
        )}
      >
        {label}
      </span>
    </span>
  );
}

export function DangerAction({
  children,
  onClick,
  label,
}: {
  children: ReactNode;
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[11px] whitespace-nowrap text-status-error hover:underline"
    >
      {children}
    </button>
  );
}
